import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Plus,
  Bug,
  type LucideIcon,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAppStore } from '@/store'
import { isAnnotatorRole } from '@/types/users'
import { useTranslation } from 'react-i18next'
import { useBugReport } from '@/components/bug-report/useBugReport'
import { getCurrentDirection } from '@/i18n/config'
import { NAV_ITEMS } from '@/components/nav-config'
import { trackEvent } from '@/utils/analytics'
import UserMenu from './UserMenu'
import { revealDataSidebar } from '@/components/DataSidebar'
import { navTarget } from '@/features/featureAccess/features'
import { useFeatureAccess } from '@/features/featureAccess/useFeatureAccess'

function SidebarNavItem({
  icon: Icon,
  label,
  comingSoonLabel,
  isActive,
  disabled,
  tooltipSide,
  onClick,
  dataTourId,
}: {
  icon: LucideIcon
  label: string
  comingSoonLabel: string
  isActive: boolean
  disabled?: boolean
  tooltipSide: 'left' | 'right'
  onClick: () => void
  /** Help-agent guided-tour anchor (see components/HelpAgent/TourOverlay). */
  dataTourId?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-tour-id={dataTourId}
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          className={`flex flex-col items-center gap-0.5 w-16 rounded-lg p-1 transition-colors ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
          }`}
          aria-current={isActive ? 'page' : undefined}
        >
          <span
            className={`flex items-center justify-center size-9 rounded-lg transition-colors ${
              isActive
                ? 'bg-sky-100 dark:bg-sky-900/30'
                : disabled
                  ? ''
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Icon className="size-4" />
          </span>
          <span
            className={`text-xs leading-4 text-center whitespace-pre-line ${
              isActive
                ? 'font-medium text-cyan-700 dark:text-cyan-300'
                : 'font-normal text-gray-900 dark:text-gray-300'
            }`}
          >
            {label}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>
        <p>{disabled ? `${label} — ${comingSoonLabel}` : label}</p>
      </TooltipContent>
    </Tooltip>
  )
}

export default function AppSidebar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { createBoard, generateDefaultBoardName, loadBoardList, user } = useAppStore()
  const { openBugReport } = useBugReport()
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'
  const tooltipSide = isRTL ? 'left' : 'right'
  const comingSoon = t('common:general.coming_soon', 'Coming soon')
  const [creatingBoard, setCreatingBoard] = useState(false)
  // Annotators are read-only outside the P&ID editor — board creation is blocked.
  const isAnnotator = isAnnotatorRole(user?.role)
  // The Workflows item follows the per-person `hide_workflows` flag only (via navTarget below).
  // The tenant switch `kg_workflows_enabled` is gone: its last server-side check was removed in
  // #36463, so with it off nothing refused a workflow action and the flag only hid the nav item.
  const { isEnabled: featureEnabled } = useFeatureAccess()
  const boardsEnabled = featureEnabled('boards')

  const handleCreateBoard = async () => {
    if (creatingBoard || isAnnotator) return
    setCreatingBoard(true)
    try {
      await loadBoardList()
      const defaultName = generateDefaultBoardName()
      const board = await createBoard(defaultName)
      if (board) {
        trackEvent('workspace-created', { workspace_id: board.id })
        navigate(`/board/${board.id}`)
      }
    } finally {
      setCreatingBoard(false)
    }
  }

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className="flex h-screen w-20 shrink-0 flex-col border-e border-[#e5e5e5] bg-[#fafafa] dark:border-dark-border dark:bg-dark-card"
        aria-label={t('nav:menu.navigation', 'Navigation')}
      >
        {/* Header — Logo */}
        <div className="flex items-center justify-center px-2 py-3.5">
          <button
            type="button"
            onClick={() => navigate('/intelligent-board')}
            className="cursor-pointer"
            aria-label={t('nav:menu.home', 'Home')}
          >
            <img
              src="/images/intuai-logo-icon.svg"
              alt="Intuigence"
              width={32}
              height={32}
              draggable={false}
            />
          </button>
        </div>

        {/* Create board button */}
        {boardsEnabled && (
          <div className="flex items-center justify-center px-2 py-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleCreateBoard}
                  disabled={creatingBoard || isAnnotator}
                  className={`flex size-9 items-center justify-center rounded-lg border border-[#e5e5e5] bg-white dark:border-dark-border dark:bg-dark-card transition-colors ${
                    creatingBoard || isAnnotator
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  aria-label={t('common:buttons.create')}
                >
                  <Plus className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side={tooltipSide}>
                <p>{t('board:actions.new_board', 'New Board')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex flex-1 flex-col items-center gap-2 px-2">
          {NAV_ITEMS.flatMap((item) => {
            const target = navTarget(item, featureEnabled)
            return target === null ? [] : [{ item, target }]
          }).map(({ item, target }) => {
            // When a doc is opened FROM a board, `DocumentPreview.tsx`
            // appends `?boardName=...` to the URL. Treat that as the
            // signal that the user is still in the board flow — keep
            // the Intelligent Board nav highlighted instead of letting
            // Data Catalog's childPaths (`/document`, `/graph-editor`,
            // `/datasheet`, `/timeseries`, ...) win. Without this the
            // highlight shifts to Data Catalog the moment the user
            // clicks a file card, which feels wrong because they're
            // still operating inside a board context.
            const cameFromBoard =
              new URLSearchParams(location.search).has('boardName')
            const isBoardNav = item.path === '/intelligent-board'
            const isDataNav = item.path === '/data-catalog'

            let isActive: boolean
            if (item.disabled) {
              isActive = false
            } else if (cameFromBoard && isBoardNav) {
              isActive = true
            } else if (cameFromBoard && isDataNav) {
              // Suppress the Data Catalog highlight while in board flow.
              isActive = false
            } else {
              isActive =
                location.pathname.startsWith(item.path) ||
                (item.childPaths?.some((p) => location.pathname.startsWith(p)) ??
                  false)
            }

            return (
              <SidebarNavItem
                key={item.path}
                icon={item.icon}
                label={t(item.sidebarLabelKey)}
                comingSoonLabel={comingSoon}
                isActive={isActive}
                disabled={item.disabled}
                tooltipSide={tooltipSide}
                onClick={() => {
                  // The Data section nav hides completely, taking its own
                  // collapse control with it. This is the way back — and the
                  // place someone looks for it once the panel is gone.
                  if (isDataNav) revealDataSidebar()
                  navigate(target)
                }}
                dataTourId={item.path === '/' ? undefined : `nav-${item.path.slice(1)}`}
              />
            )
          })}
        </nav>

        {/* Footer */}
        <div className="flex flex-col items-center gap-2 px-2 pb-3 pt-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={openBugReport}
                className="flex size-9 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t('common:tooltips.report_bug')}
              >
                <Bug className="size-4 text-gray-900 dark:text-gray-400" />
              </button>
            </TooltipTrigger>
            <TooltipContent side={tooltipSide}>
              <p>{t('common:tooltips.report_bug')}</p>
            </TooltipContent>
          </Tooltip>

          <UserMenu tooltipSide={tooltipSide} />
        </div>
      </aside>
    </TooltipProvider>
  )
}
